/* @flow */

import * as React from 'react';
import { pure } from 'recompose';
import styled from '@emotion/styled';
import { borderWidth, padding, tint } from 'polished';
import numeral from 'numeral';

import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import PlayerBar from 'app/components/challenge/PlayerBar';
import PlayerDatum from 'app/components/challenge/PlayerDatum';
import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import StatusIcon from 'app/components/challenge/StatusIcon';
import StarSymbol from '../challenge/StarSymbol';
import PlayerCell from './PlayerCell';
import { isInProgress } from '../../utils/challengeUtils';

type Props = {
  awayTeam?: string,
  exposure: number,
  gameOrder?: number,
  homeTeam?: string,
  image?: string,
  initials?: string,
  isHome: boolean,
  isRacing?: boolean,
  isScratched?: boolean,
  labels: Array<*>,
  name: string,
  onClick: () => void,
  place?: number,
  position?: string,
  reverseOrder?: boolean,
  rider?: string,
  riderImage?: string,
  salary: number,
  selected?: boolean,
  statusId: number,
  trainer?: string,
  entryCount: number,
  win?: number
};

const defaultProps = {
  awayTeam: '',
  gameOrder: undefined,
  homeTeam: '',
  image: undefined,
  initials: undefined,
  isRacing: false,
  isScratched: false,
  place: undefined,
  position: undefined,
  reverseOrder: false,
  rider: undefined,
  riderImage: undefined,
  selected: false,
  trainer: undefined,
  win: undefined
};

const Row = styled.div(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: 'white',
  borderColor: theme.color.basis5,
  borderStyle: 'solid',
  ...borderWidth(0, 0, 1),
  display: 'flex',
  minWidth: 0,
  ...padding('0.5em', 0)
}));

const TableRow = styled.div(
  ({ theme }) => ({
    backgroundColor: 'white',
    borderColor: theme.color.basis6,
    borderStyle: 'solid',
    ...borderWidth(1, 0),
    display: 'table-row'
  }),
  ({ selected, theme }) =>
    selected
      ? {
          backgroundColor: theme.color.primary,
          color: 'white'
        }
      : {
          '&:hover': {
            backgroundColor: tint(0.25, theme.color.primary)
          }
        }
);

const PlayerData = styled.div({
  display: 'flex',
  marginRight: '-0.5em'
});

const Cell = styled.div({
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'center',
  ...padding(0, 5)
});

const MainCell = styled.div({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  overflow: 'hidden'
});

const Salary = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.medium,
  textAlign: 'right'
}));

const Final = styled.div(({ theme }) => ({
  display: 'none',
  height: '100%',
  textAlign: 'center',
  verticalAlign: 'middle',
  width: '2rem',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'table-cell'
  }
}));

const Arrow = styled.div({
  alignItems: 'center',
  display: 'flex',
  fontSize: '2rem',
  justifyContent: 'center'
});

const ArrowCircle = styled.span(({ theme }) => ({
  alignItems: 'center',
  color: theme.color.primary,
  display: 'flex',
  fontSize: '2rem',
  justifyContent: 'center',
  ...padding(0, 5)
}));

const StatusIconWrapper = styled.div(({ live, theme }) => ({
  backgroundImage: `linear-gradient(135deg, ${
    live ? theme.color.primary : theme.color.basis1
  } 50%, transparent 50%)`,
  color: 'white',
  fontSize: '0.5rem',
  height: '2.2em',
  left: 0,
  padding: 1,
  transform: 'rotate(360deg)',
  position: 'absolute',
  top: 0,
  width: '2.2em'
}));

const PlayerAvatarCell = styled(PlayerCell)({
  position: 'relative',
  width: 50
});

function PlayerListRow({
  awayTeam,
  exposure,
  gameOrder,
  homeTeam,
  image,
  initials,
  isHome,
  isRacing,
  isScratched,
  labels,
  name,
  onClick,
  place,
  position,
  reverseOrder,
  rider,
  riderImage,
  salary,
  selected,
  statusId,
  trainer,
  entryCount,
  win
}: Props) {
  const avatarImage = isRacing ? riderImage : image;

  return (
    <Responsive tabletLandscape desktop>
      {isLargeScreen =>
        isLargeScreen ? (
          <Tappable component={TableRow} onTap={onClick} selected={selected}>
            <PlayerAvatarCell bare>
              <StatusIconWrapper live={isInProgress(statusId)}>
                <StatusIcon statusId={statusId} />
              </StatusIconWrapper>
              <PlayerAvatar fallbackText={initials} image={avatarImage} />
            </PlayerAvatarCell>
            <PlayerCell bare width="100%">
              <PlayerBar
                awayTeam={awayTeam}
                gameOrder={gameOrder}
                homeTeam={homeTeam}
                labels={labels}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                name={name}
                position={position}
                trainer={trainer}
                reverseOrder={reverseOrder}
                rider={rider}
              />
            </PlayerCell>
            <PlayerCell textAlign="center">{entryCount}</PlayerCell>
            <PlayerCell>{exposure}%</PlayerCell>
            {isRacing ? (
              <React.Fragment>
                <PlayerCell bold>
                  <span>{numeral(win).format('0.00')}</span>
                  <StarSymbol />
                </PlayerCell>
                <PlayerCell bold>
                  <span>{numeral(place).format('0.00')}</span>
                  <StarSymbol />
                </PlayerCell>
              </React.Fragment>
            ) : (
              <PlayerCell bold>{numeral(salary).format('$0,0')}</PlayerCell>
            )}
            {selected && (
              <Final>
                <Arrow className="ion-ios-arrow-forward" />
              </Final>
            )}
          </Tappable>
        ) : (
          <Row>
            <Cell>
              <PlayerAvatar fallbackText={initials} image={avatarImage} />
            </Cell>
            <MainCell>
              <PlayerBar
                awayTeam={awayTeam}
                homeTeam={homeTeam}
                gameOrder={gameOrder}
                labels={labels}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                name={name}
                place={place}
                position={position}
                reverseOrder={reverseOrder}
                rider={rider}
                trainer={trainer}
                win={win}
              />
            </MainCell>
            <Cell>
              {!isRacing && <Salary>{numeral(salary).format('$0,0')}</Salary>}
              <PlayerData>
                <PlayerDatum label="#Sel" textAlign="center">
                  {entryCount || '\u2012'}
                </PlayerDatum>
                <PlayerDatum label="Exp%" textAlign="left">
                  {exposure ? `${exposure}%` : '\u2012'}
                </PlayerDatum>
              </PlayerData>
            </Cell>
            <Tappable component={Cell} onTap={onClick}>
              <ArrowCircle className="ion-android-arrow-dropright-circle" />
            </Tappable>
          </Row>
        )
      }
    </Responsive>
  );
}

PlayerListRow.defaultProps = defaultProps;

export default pure(PlayerListRow);
